import React, { useMemo } from 'react';
import { useTheme } from '@mui/styles';
import { EsPCAReportField, PCAReportCountField, PCAReportMetric } from 'c-sdk';
import type { ChartReportPart, ReportProps } from 'c-reports/Types';
import { ReportDateTimeframe, ReportType } from 'c-reports/Types';
import { ReportWrapper } from 'c-reports/Components';
import type { ByFieldChartProps } from 'c-main/Components/Campaign/CampaignReports/types';
import { dateSortArray } from 'c-lib';
import { ChartDataKeyDateFormat } from 'c-reports/Generators';
import useChartColors from 'c-reports/useChartColors';
import type { YAxisFloor } from 'c-reports/yAxisDomain';
import useCampaignWrapperState from 'c-main/Components/Campaign/CampaignReports/useCampaignWrapperState';

import {
    ByDayByHourOverall,
    ByFieldDaily,
    ByFieldHourly,
    ByFieldOverall,
} from '../byFieldDataMutators';

// Narrow opt-out from the 0-floored Y axis, for the delivered-vs-expected over-time chart
// only. Starting at 0 squashes the two lines together at the top and hides the gap between
// them, which is the whole reading of that chart. Scoped to the campaign and media owner
// below, on the Plays tab only, rather than applied generally, so nothing else moves.
//
// NOTE: campaign ids are per-environment. 10630 is the id in one database and will not be
// the same campaign elsewhere, so this needs checking wherever it is expected to take
// effect. Widen or empty these lists to change the scope.
const SCALE_TO_DATA_CAMPAIGN_IDS = [10630];
const SCALE_TO_DATA_OWNERS = ['New Tradition Media'];

type Props = ByFieldChartProps & { showExpectedLines?: boolean; filterExtras: React.ReactNode[] };

const OverTimeCharts: React.FC<Props> = ({
    pcaData,
    field,
    timeFrame,
    actualSelectedFieldParts,
    fieldLabel,
    expectedLabel,
    percentageLabel,
    actualLabel,
    dateLabelLabel,
    hourLabel,
    performanceLabel,
    missingLabel,
    inScheduleLabel,
    outScheduleLabel,
    unbookedLabel,
    renderTooltipLabel,
    reportWrapperProps,
    countFields,
    lineChartAccumulative,
    metric,
    showExpectedLines = false,
    campaignId,
    filterExtras,
}) => {
    const theme = useTheme();
    const data = useMemo(() => {
        let chartData = [];
        if (field == null) {
            if (timeFrame === ReportDateTimeframe.Daily) {
                chartData = ByDayByHourOverall(
                    pcaData,
                    actualLabel,
                    percentageLabel,
                    expectedLabel,
                    missingLabel,
                    hourLabel,
                    inScheduleLabel,
                    outScheduleLabel,
                    unbookedLabel,
                    lineChartAccumulative,
                ).sort((a, b) => +a[hourLabel] - +b[hourLabel]);
            } else {
                chartData = ByFieldOverall(
                    pcaData,
                    actualSelectedFieldParts,
                    actualLabel,
                    percentageLabel,
                    expectedLabel,
                    missingLabel,
                    dateLabelLabel,
                    inScheduleLabel,
                    outScheduleLabel,
                    unbookedLabel,
                    lineChartAccumulative,
                ).sort(dateSortArray(dateLabelLabel, ChartDataKeyDateFormat));
            }
        } else if (
            timeFrame === ReportDateTimeframe.Weekly ||
            timeFrame === ReportDateTimeframe.BiWeekly ||
            timeFrame === ReportDateTimeframe.Monthly ||
            timeFrame === ReportDateTimeframe.Range ||
            timeFrame === ReportDateTimeframe.All
        ) {
            chartData = ByFieldDaily(
                pcaData,
                actualSelectedFieldParts,
                actualLabel,
                percentageLabel,
                expectedLabel,
                fieldLabel,
                dateLabelLabel,
                lineChartAccumulative,
            );
        } else if (timeFrame === ReportDateTimeframe.Daily) {
            chartData = ByFieldHourly(
                pcaData,
                actualSelectedFieldParts,
                actualLabel,
                percentageLabel,
                expectedLabel,
                fieldLabel,
                hourLabel,
                lineChartAccumulative,
            );
        }
        if (fieldLabel === 'Creative Name') {
            chartData = chartData.filter(item => item[fieldLabel] !== '');
        }
        if (fieldLabel === 'Creative Name') {
            chartData = chartData.map(item => {
                const filteredItem = Object.fromEntries(
                    Object.entries(item).filter(([key]) => key !== ''),
                );
                return filteredItem;
            });
        }

        return chartData;
    }, [
        field,
        timeFrame,
        pcaData,
        actualSelectedFieldParts,
        actualLabel,
        percentageLabel,
        expectedLabel,
        missingLabel,
        fieldLabel,
        dateLabelLabel,
        hourLabel,
        inScheduleLabel,
        outScheduleLabel,
        unbookedLabel,
        lineChartAccumulative,
    ]);

    const xAxisDataKey = useMemo<string>(() => {
        if (
            timeFrame === ReportDateTimeframe.Weekly ||
            timeFrame === ReportDateTimeframe.BiWeekly ||
            timeFrame === ReportDateTimeframe.Monthly ||
            timeFrame === ReportDateTimeframe.Range ||
            timeFrame === ReportDateTimeframe.All
        ) {
            return dateLabelLabel;
        }
        if (timeFrame === ReportDateTimeframe.Daily) {
            return hourLabel;
        }

        return '';
    }, [dateLabelLabel, hourLabel, timeFrame]);

    const chartColors = useChartColors();

    const parts = useMemo<ChartReportPart[]>(() => {
        if (field == null) {
            const parts = [];

            if (countFields.indexOf(PCAReportCountField.InSchedule) !== -1) {
                parts.push({
                    dataKey: expectedLabel,
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    lineProps: { stroke: theme.palette.grey['500'], strokeDasharray: '5 5' },
                });
            }

            parts.push({
                dataKey: actualLabel,
                lineProps: { stroke: theme.palette.primary.main },
            });
            return parts;
        }

        return actualSelectedFieldParts.reduce((acc, curr, index) => {
            acc.push({
                dataKey: curr,
                lineProps: { stroke: chartColors[index % chartColors.length] },
            });

            if (showExpectedLines) {
                acc.push({
                    dataKey: `${curr} ${expectedLabel}`,
                    lineProps: { stroke: chartColors[index], strokeDasharray: '5 5' },
                });
            }

            return acc;
        }, [] as ChartReportPart[]);
    }, [
        actualLabel,
        actualSelectedFieldParts,
        expectedLabel,
        field,
        theme.palette.grey,
        theme.palette.primary.main,
        countFields,
        showExpectedLines,
        chartColors,
    ]);

    // See SCALE_TO_DATA_* above. Every other chart keeps the 0 floor.
    const yAxisFloor = useMemo<YAxisFloor>(() => {
        if (!SCALE_TO_DATA_CAMPAIGN_IDS.includes(campaignId)) return 'zero';
        if (metric !== PCAReportMetric.Plays) return 'zero';
        if (field !== EsPCAReportField.DisplayOwner) return 'zero';

        const isExpected = (key: string) =>
            key === expectedLabel || key.endsWith(` ${expectedLabel}`);
        const keys = parts.map(part => String(part.dataKey));
        const deliveredKeys = keys.filter(key => !isExpected(key));

        const isSingleScaledOwner =
            deliveredKeys.length === 1 && SCALE_TO_DATA_OWNERS.includes(deliveredKeys[0]);

        return keys.some(isExpected) && isSingleScaledOwner ? 'nearData' : 'zero';
    }, [parts, expectedLabel, field, campaignId, metric]);

    const reportProps = useMemo<ReportProps<unknown>[]>(
        () => [
            {
                type: ReportType.Line,
                namePrefix: `${campaignId}_${metric}_${field}_overtime`,
                xAxisDataKey,
                data,
                parts,
                renderTooltipLabel,
                yAxisFloor,
                yAxisTickFormatterAsTime: metric === PCAReportMetric.Time,
            },
        ],
        [campaignId, data, field, metric, parts, renderTooltipLabel, xAxisDataKey, yAxisFloor],
    );
    const { onResetAdvancedFilters } = useCampaignWrapperState(campaignId);

    return (
        <ReportWrapper
            reportProps={reportProps}
            onResetAdvancedFilters={onResetAdvancedFilters}
            filterExtras={filterExtras}
            {...reportWrapperProps}
        />
    );
};

export default OverTimeCharts;
